import type { KeyboardEvent } from 'react';
import { memo, useCallback, useState } from 'react';

import type { DntFieldKind } from '@/components/integrations/dntValidation';
import {
  MAX_ENTRIES,
  normalizeEntry,
  validateEntry,
} from '@/components/integrations/dntValidation';
import { BadgeWithButton } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { Input } from '@/lib/base/forms/input/input';

export interface JoinExclusionEditorProps {
  emails: string[];
  domains: string[];
  onEmailsChange: (next: string[]) => void;
  onDomainsChange: (next: string[]) => void;
}

const MAX_ENTRIES_ERROR = `You can add up to ${MAX_ENTRIES} entries.`;

interface ChipProps {
  entry: string;
  onRemove: (entry: string) => void;
}

const Chip = memo(function Chip({ entry, onRemove }: ChipProps) {
  const handleClick = useCallback(() => onRemove(entry), [onRemove, entry]);
  return (
    <BadgeWithButton
      type='pill-color'
      size='sm'
      color='gray'
      buttonLabel={`Remove ${entry}`}
      onButtonClick={handleClick}
    >
      {entry}
    </BadgeWithButton>
  );
});

export function JoinExclusionEditor({
  emails,
  domains,
  onEmailsChange,
  onDomainsChange,
}: JoinExclusionEditorProps) {
  const [emailInput, setEmailInput] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [domainInput, setDomainInput] = useState('');
  const [domainError, setDomainError] = useState<string | null>(null);

  const addEntry = useCallback(
    (kind: DntFieldKind) => {
      const input = kind === 'email' ? emailInput : domainInput;
      const list = kind === 'email' ? emails : domains;
      const setInput = kind === 'email' ? setEmailInput : setDomainInput;
      const setError = kind === 'email' ? setEmailError : setDomainError;
      const onChange = kind === 'email' ? onEmailsChange : onDomainsChange;

      const result = validateEntry(input, kind);
      if (!result.isValid) {
        setError(result.error ?? 'Invalid entry');
        return;
      }
      if (list.length >= MAX_ENTRIES) {
        setError(MAX_ENTRIES_ERROR);
        return;
      }

      const normalized = normalizeEntry(input);
      setInput('');
      setError(null);
      if (list.includes(normalized)) {
        return;
      }
      onChange([...list, normalized]);
    },
    [emailInput, domainInput, emails, domains, onEmailsChange, onDomainsChange]
  );

  const handleAddEmail = useCallback(() => addEntry('email'), [addEntry]);
  const handleAddDomain = useCallback(() => addEntry('domain'), [addEntry]);

  const handleRemoveEmail = useCallback(
    (entry: string) => onEmailsChange(emails.filter(e => e !== entry)),
    [emails, onEmailsChange]
  );
  const handleRemoveDomain = useCallback(
    (entry: string) => onDomainsChange(domains.filter(e => e !== entry)),
    [domains, onDomainsChange]
  );

  const handleEmailInputChange = useCallback((value: string) => {
    setEmailInput(value);
  }, []);
  const handleDomainInputChange = useCallback((value: string) => {
    setDomainInput(value);
  }, []);

  const handleEmailKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddEmail();
      }
    },
    [handleAddEmail]
  );
  const handleDomainKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAddDomain();
      }
    },
    [handleAddDomain]
  );

  return (
    <div className='space-y-4'>
      <section aria-labelledby='join-exclusion-email-heading'>
        <p
          id='join-exclusion-email-heading'
          className='text-sm font-medium text-primary mb-1'
        >
          Excluded email addresses
        </p>
        <ul role='list' className='flex flex-wrap gap-2 mb-2'>
          {emails.length === 0 ? (
            <li className='text-xs text-tertiary'>No entries.</li>
          ) : (
            emails.map(entry => (
              <li key={entry}>
                <Chip entry={entry} onRemove={handleRemoveEmail} />
              </li>
            ))
          )}
        </ul>
        <div className='flex items-end gap-2'>
          <div className='flex-1'>
            <Input
              label='Add email address'
              placeholder='name@example.com'
              value={emailInput}
              onChange={handleEmailInputChange}
              onKeyDown={handleEmailKeyDown}
              isInvalid={emailError !== null}
              showError={emailError !== null}
              hint={emailError}
            />
          </div>
          <Button color='secondary' size='sm' onClick={handleAddEmail}>
            Add
          </Button>
        </div>
      </section>

      <section aria-labelledby='join-exclusion-domain-heading'>
        <p
          id='join-exclusion-domain-heading'
          className='text-sm font-medium text-primary mb-1'
        >
          Excluded domains
        </p>
        <ul role='list' className='flex flex-wrap gap-2 mb-2'>
          {domains.length === 0 ? (
            <li className='text-xs text-tertiary'>No entries.</li>
          ) : (
            domains.map(entry => (
              <li key={entry}>
                <Chip entry={entry} onRemove={handleRemoveDomain} />
              </li>
            ))
          )}
        </ul>
        <div className='flex items-end gap-2'>
          <div className='flex-1'>
            <Input
              label='Add domain'
              placeholder='example.com'
              value={domainInput}
              onChange={handleDomainInputChange}
              onKeyDown={handleDomainKeyDown}
              isInvalid={domainError !== null}
              showError={domainError !== null}
              hint={domainError}
            />
          </div>
          <Button color='secondary' size='sm' onClick={handleAddDomain}>
            Add
          </Button>
        </div>
      </section>
    </div>
  );
}
